'use client';

import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { useGeolocation } from '@domain/geolocation/GeolocationContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { SpeciesListNodeFields } from '../../generated/graphql';
import { getTopSpeciesNearLocation } from '@graphql/queries/rutilus/Species';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { InfoIcon } from '../Icons/InfoIcon';
import { LocationInfoModal } from '../LocationInfoModal/LocationInfoModal';
import { Text } from '../Text/Text';
import { TopSpeciesCTA } from '../TopSpeciesCTA/TopSpeciesCTA';
import { NearbyTopSpeciesCards } from './NearbyTopSpeciesCards';
import { NearbyTopSpeciesLoader } from './NearbyTopSpeciesLoader';
import { skipToken } from '@apollo/client/react';

interface IProps {
  className?: string;
  defaultTopSpeciesList: SpeciesListNodeFields[];
  isUA?: boolean;
}

const RADIUS_OF_SPECIES_IN_KM = 50;
const NUMBER_OF_SPECIES_TO_SHOW = 5;

export const NearbyTopSpecies = ({ className, defaultTopSpeciesList, isUA = false }: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [visibleItems, setVisibleItems] = useState(NUMBER_OF_SPECIES_TO_SHOW);
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);

  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const { isLoading: currentLocationLoading, location } = useGeolocation();

  const country = location?.countryCode;
  const region = location?.regionName;

  const shouldSkip = currentLocationLoading || !location?.coordinates;

  const queryOptions = shouldSkip
    ? skipToken
    : {
        variables: {
          first: 10,
          boundingCircle: {
            center: location.coordinates,
            delta: RADIUS_OF_SPECIES_IN_KM,
          },
        },
      };

  const { data, loading } = useRutilusQuery(getTopSpeciesNearLocation, queryOptions);

  const speciesList = useMemo(() => getNodesFromConnection(data?.topSpeciesNearLocation), [data]);
  const isLoading = !clientHasRendered || currentLocationLoading || loading;
  const hasSpeciesList = !isLoading && speciesList.length > 0;
  const HeadingText = hasSpeciesList ? 'Top fish species near you' : 'Top fish species';

  const handleShowMore = () => {
    // First click -> show all species
    if (visibleItems < speciesList.length) {
      setVisibleItems(prev => prev + NUMBER_OF_SPECIES_TO_SHOW);
      // Second click -> show download CTA
    } else {
      setShowDownloadCTA(true);
      return;
    }
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyTopSpecies',
        // source_id: placeId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyTopSpecies',
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const handleInfoButtonClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyTopSpecies',
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsInfoModalOpen(true);
  };

  return (
    <div className={twMerge('flex flex-col gap-2', className)}>
      <Heading className="m-0" variant="sm" as="h2">
        {HeadingText}
      </Heading>

      {isLoading ? (
        <NearbyTopSpeciesLoader numberOfLoaders={NUMBER_OF_SPECIES_TO_SHOW} />
      ) : (
        <>
          {hasSpeciesList && region && country && (
            <div className="flex items-center gap-1">
              <Text variant="sm" className="mb-0">
                Result for {region}, {country}
              </Text>
              <Button
                variant="textNone"
                className="body-text-sm flex"
                onClick={handleInfoButtonClick}
                aria-label="Show information about location results"
              >
                <InfoIcon width={16} height={16} />
              </Button>
            </div>
          )}
          <div className="mt-2 flex flex-col">
            <NearbyTopSpeciesCards
              speciesList={hasSpeciesList ? speciesList : defaultTopSpeciesList}
              visibleItems={visibleItems}
            />
            {showDownloadCTA ? (
              <TopSpeciesCTA onCTAClick={handleCTAClick} isUA={isUA} />
            ) : (
              <Button
                size="sm"
                className="mt-2 p-2"
                variant="textPrimary"
                onClick={handleShowMore}
                aria-label="Show more species"
              >
                Show more species
              </Button>
            )}
          </div>
        </>
      )}

      <AppDownloadModal
        appDownloadSource="NearbyTopSpecies"
        modalHeader={`Download the Fishbrain app to see all species near you`}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        isUA={isUA}
      />
      <LocationInfoModal
        isModalOpen={infoModalOpen}
        onCloseModal={() => {
          setIsInfoModalOpen(false);
        }}
        radius={RADIUS_OF_SPECIES_IN_KM}
      />
    </div>
  );
};
